import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Eye, ThumbsDown, ThumbsUp } from 'lucide-react';
import { RegulationLink } from '../ui/RegulationLink';
export const AO_URL = 'https://www.fda.gov.ph/wp-content/uploads/2021/03/Administrative-Order-No.-2014-0030.pdf';
export const CIRCULAR_URL = 'https://www.fda.gov.ph/fda-circular-no-2023-009-adoption-of-2015-philippine-dietary-reference-intakes-pdri-percent-recommended-energy-intakes-recommended-nutrient-intakes-rei-rni-as-the-new-dietary-standard-of-all-pr/';
export type PhStatus = 'Fail' | 'Warning' | 'Pass' | 'Skip' | 'Review' | 'Inconclusive' | 'Present' | 'Missing' | 'Needs review';
export type PhFinding = {
  name: string;
  status: PhStatus;
  issue?: string;
  recommendation?: string;
  note?: string;
  explanation?: string;
  confirmation?: string;
  checklist?: string[];
  citation: {
    text: string;
    url?: string;
  };
};
export type PhSection = {
  title: string;
  checks: string;
  chips: string[];
  verdictNoun?: string;
  findings: PhFinding[];
};
const PASS_CONFIRMATION = 'No compliance issue identified.';
export function PhFindingSection({
  section,
  defaultOpen = false
}: {
  section: PhSection;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const count = (...statuses: PhStatus[]) => section.findings.filter(finding => statuses.includes(finding.status)).length;
  const fails = count('Fail', 'Missing');
  const warnings = count('Warning', 'Needs review');
  const passes = count('Pass', 'Present');
  const inconclusive = count('Inconclusive');
  const skips = count('Skip');
  const scored = fails + warnings + passes;
  const reviewOnly = section.findings.every(finding => finding.status === 'Review');
  const issues = fails + warnings;
  const noun = section.verdictNoun ?? 'PASS';
  const failLabel = noun === 'PRESENT' ? 'MISSING' : 'FAIL';
  const warnLabel = noun === 'PRESENT' ? 'NEEDS REVIEW' : 'WARNING';
  const edge = reviewOnly ? 'border-l-sky-500' : fails > 0 ? 'border-l-red-500' : warnings > 0 ? 'border-l-amber-400' : 'border-l-[#dce3da]';
  const chips = reviewOnly ? section.chips : [...(fails ? [`${fails} ${failLabel}`] : []), ...(warnings ? [`${warnings} ${warnLabel}`] : []), `${passes}/${scored} ${noun}`, ...(inconclusive ? [`${inconclusive} INCONCLUSIVE`] : []), ...(skips ? [`${skips} SKIP`] : [])];
  return <section className={`overflow-hidden rounded-2xl border border-[#dce3da] border-l-4 ${edge} bg-white shadow-sm`} data-id="element-1792" data-mp="2vrk98tiqceg4">
      <button type="button" onClick={() => setOpen(state => !state)} aria-expanded={open} className="flex w-full flex-col gap-3 px-5 py-4 text-left transition hover:bg-[#f8faf7] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-forest-green lg:flex-row lg:items-center lg:justify-between" data-id="element-1793" data-mp="2qtipe6dhcb3l">
        <span className="min-w-0" data-id="element-1794" data-mp="3agq6iby0buld">
          <span className="block font-[Figtree] text-base font-semibold text-evergreen" data-id="element-1795" data-mp="3ahrh6fb42f2k">{section.title}</span>
          <span className="mt-0.5 block font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-[#8b968d]" data-id="element-1796" data-mp="3t6bqka1d0dyc">
            {reviewOnly ? section.checks : `${issues} ${issues === 1 ? 'issue' : 'issues'}`}
          </span>
        </span>
        <span className="flex flex-wrap items-center gap-2" data-id="element-1797" data-mp="3k6ol9d5ieo00">
          {chips.map(chip => <Chip key={chip} label={chip} data-id="element-1798" data-mp="3p59rbt7g01wd" />)}
          <ChevronDown className={`h-5 w-5 text-[#718174] transition ${open ? 'rotate-180' : ''}`} data-id="element-1799" data-mp="3adr1l3os6szt" />
        </span>
      </button>
      {open && <div className="divide-y divide-[#edf0eb] border-t border-[#e5ebe3]" data-id="element-1800" data-mp="31duj5jsziqd7">
          {section.findings.map(finding => <PhFindingRow key={finding.name} finding={finding} data-id="element-1801" data-mp="35eo6oc2im35k" />)}
        </div>}
    </section>;
}
function Chip({
  label
}: {
  label: string;
}) {
  const tone = label.includes('FAIL') || label.includes('MISSING') ? 'bg-red-100 text-red-800' : label.includes('WARNING') || label.includes('NEEDS REVIEW') ? 'bg-amber-100 text-amber-800' : label.includes('PASS') || label.includes('PRESENT') ? 'bg-forest-green/10 text-forest-green' : label.includes('REVIEW') ? 'bg-sky-100 text-sky-800' : 'bg-slate-100 text-slate-600';
  return <span className={`rounded-full px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] ${tone}`} data-id="element-1802" data-mp="3l1ap9vcsdbo4">{label}</span>;
}
function PhFindingRow({
  finding
}: {
  finding: PhFinding;
}) {
  const [open, setOpen] = useState(false);
  const [showLogic, setShowLogic] = useState(false);
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const extract = finding.issue ?? finding.explanation ?? finding.confirmation ?? PASS_CONFIRMATION;
  const isReview = finding.status === 'Review';
  return <div className={isReview ? 'bg-sky-50/70' : undefined} data-id="element-1803" data-mp="158ldrlhjseiu">
      <button type="button" onClick={() => setOpen(state => !state)} aria-expanded={open} className={`flex w-full items-center gap-3 px-5 py-3.5 text-left transition ${isReview ? 'hover:bg-sky-100/60' : 'hover:bg-[#fafcf9]'}`} data-id="element-1804" data-mp="10atdo46r1m3k">
        <StatusPill status={finding.status} data-id="element-1805" data-mp="1cr2v2dvo0xmd" />
        <span className="min-w-0 flex-1" data-id="element-1806" data-mp="1hjcjfph4tpmj">
          <span className={`block font-[Figtree] text-sm font-semibold ${finding.status === 'Skip' ? 'text-[#7d8c80]' : 'text-evergreen'}`} data-id="element-1807" data-mp="1hknumacwpzno">
            {finding.name}
          </span>
          {finding.note && <span className="mt-1 block text-xs italic leading-relaxed text-[#8b968d]" data-id="element-1808" data-mp="ox1g47255am1">{finding.note}</span>}
        </span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-[#718174] transition ${open ? 'rotate-180' : ''}`} data-id="element-1809" data-mp="tw4kdpaarcsy" />
      </button>

      {open && <div className={`space-y-4 border-t px-5 pb-5 pt-4 ${isReview ? 'border-sky-100 bg-sky-50' : 'border-[#eef2ec] bg-[#fbfdf9]'}`} data-id="element-1810" data-mp="12w155ftkfriq">
          <button type="button" className="inline-flex items-center gap-1.5 rounded-full border border-[#d6dfd3] bg-white px-2.5 py-1 text-xs font-medium text-evergreen" data-id="element-1811" data-mp="2e9lq5ushe1lw">
            AI agent
            <ThumbsUp className="h-3.5 w-3.5" data-id="element-1812" data-mp="a5ihg4ewv55f" />
            <ThumbsDown className="h-3.5 w-3.5" data-id="element-1813" data-mp="o631f3wkb70i" />
          </button>

          {isReview ? <div data-id="element-1814" data-mp="o3p8b0lg04vl">
              <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-sky-700" data-id="element-1815" data-mp="j78atfscse5u">Explanation</p>
              <p className="mt-1 max-w-3xl text-sm leading-relaxed text-[#4a5a63]" data-id="element-1816" data-mp="3rm0b7bqtacyb">{finding.explanation}</p>
            </div> : <div data-id="element-1817" data-mp="3nj3q9edsu4r7">
              <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-[#7a897d]" data-id="element-1818" data-mp="5777zusj2wnq">Regulatory reference</p>
              <p className="mt-1 font-mono text-sm text-evergreen" data-id="element-1819" data-mp="g8okmc8tig9">{finding.citation.text}</p>
              <blockquote className="mt-3 max-w-3xl border-l-2 border-lime-glow pl-3 text-sm italic leading-relaxed text-[#617366]" data-id="element-1820" data-mp="38vsi7mgtt2vu">
                “{extract}”
              </blockquote>
              {finding.recommendation && <p className="mt-3 max-w-3xl text-sm leading-relaxed text-[#506355]" data-id="element-1821" data-mp="1je3wd5o5kptd">{finding.recommendation}</p>}
            </div>}

          {finding.checklist && <div data-id="element-1822" data-mp="1af0da1ozkaev">
              <p className={`font-mono text-[10px] font-semibold uppercase tracking-[0.15em] ${isReview ? 'text-sky-700' : 'text-[#7a897d]'}`} data-id="element-1823" data-mp="1afjlgqou3kfp">
                Verification checklist
              </p>
              <ul className="mt-2 max-w-3xl space-y-2" data-id="element-1824" data-mp="2347cz406f61p">
                {finding.checklist.map(item => <li key={item} data-id="element-1825" data-mp="273g6c7rb11wq">
                    <label className="flex cursor-pointer items-start gap-2.5 text-sm leading-relaxed text-[#506355]" data-id="element-1826" data-mp="272esxwmtg3bu">
                      <input type="checkbox" checked={Boolean(checked[item])} onChange={() => setChecked(state => ({
                ...state,
                [item]: !state[item]
              }))} className="mt-0.5 h-4 w-4 shrink-0 rounded border-[#c2cdc1] text-forest-green focus:ring-forest-green" data-id="element-1827" data-mp="1y3t0n9rjl9xv" />
                      <span className={checked[item] ? 'text-[#8b968d] line-through' : undefined} data-id="element-1828" data-mp="1ea4fs4va9oqu">{item}</span>
                    </label>
                  </li>)}
              </ul>
            </div>}

          <div data-id="element-1829" data-mp="zfgg57ckdkj5">
            <button type="button" onClick={() => setShowLogic(state => !state)} aria-expanded={showLogic} className="inline-flex items-center gap-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-[#6f8174] transition hover:text-forest-green" data-id="element-1830" data-mp="zfzygp64maxp">
              <ChevronRight className={`h-3.5 w-3.5 transition ${showLogic ? 'rotate-90' : ''}`} data-id="element-1831" data-mp="1nbceup5mitlz" />
              Show verification logic
            </button>
            {showLogic && <p className="mt-2 max-w-3xl rounded-lg bg-white px-3 py-2 text-sm leading-relaxed text-[#617366] ring-1 ring-[#e5ebe3]" data-id="element-1832" data-mp="2fv15jtiva2xe">
                Extracted label text for this element was matched against the referenced clause, then evaluated against the clause&apos;s
                numeric and textual conditions. The verdict shown is the deterministic result of that comparison.
              </p>}
          </div>

          <div data-id="element-1833" data-mp="2seetv8cmuoak">
            <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-[#7a897d]" data-id="element-1834" data-mp="2xavngi2lpuv1">Citations</p>
            <div className="mt-2 flex flex-wrap items-center gap-2" data-id="element-1835" data-mp="32b1fw0gp3le6">
              <RegulationLink text={finding.citation.text} href={finding.citation.url} data-id="element-1836" data-mp="36btnyqdtzk8v" />
              <span className="rounded-full bg-[#eef3ec] px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.13em] text-[#6f8174]" data-id="element-1837" data-mp="369h2i38v6rr2">
                Preview
              </span>
            </div>
            <blockquote className="mt-2 max-w-3xl border-l-2 border-[#dce3da] pl-3 text-sm italic leading-relaxed text-[#748477]" data-id="element-1838" data-mp="3l0znkfgkzcha">
              “{extract}”
            </blockquote>
          </div>
        </div>}
    </div>;
}
function StatusPill({
  status
}: {
  status: PhStatus;
}) {
  const styles: Record<PhStatus, string> = {
    Fail: 'bg-red-100 text-red-700',
    Missing: 'bg-red-100 text-red-700',
    Warning: 'bg-amber-100 text-amber-800',
    'Needs review': 'bg-amber-100 text-amber-800',
    Pass: 'bg-forest-green/10 text-forest-green',
    Present: 'bg-forest-green/10 text-forest-green',
    Inconclusive: 'bg-slate-100 text-slate-600',
    Skip: 'bg-slate-50 text-slate-400',
    Review: 'border border-sky-300 bg-white text-sky-700'
  };
  if (status === 'Review') return <span className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${styles.Review}`} data-id="element-1839" data-mp="14exqw5sg4qat">
        <Eye className="h-3 w-3" data-id="element-1840" data-mp="19chehwb1afff" />
        Needs Manual Review
      </span>;
  return <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${styles[status]}`} data-id="element-1841" data-mp="366ucy9c8piju">● {status}</span>;
}